/**
 * @fileoverview Simple preview table for CSV header and first rows.
 * @module features/universal-transactions/components/upload/PreviewTable
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { MappingDefinition } from '../../schemas/universal-transactions'

export interface PreviewTableProps {
  header: string[]
  rows: Record<string, string>[]
  mapping?: MappingDefinition | undefined
}

export function PreviewTable({ header, rows, mapping }: PreviewTableProps): ReactElement {
  const { t } = useTranslation('features-universal-transactions')

  const requiredFields = ['txn_date', 'amount', 'currency', 'title']
  const fieldLabels: Record<string, string> = {
    txn_date: t('mapping.fields.txnDate', { defaultValue: 'Transaction Date' }),
    amount: t('mapping.fields.amount', { defaultValue: 'Amount' }),
    currency: t('mapping.fields.currency', { defaultValue: 'Currency' }),
    title: t('mapping.fields.title', { defaultValue: 'Title' }),
    counterparty_name: t('mapping.fields.counterparty', { defaultValue: 'Counterparty' }),
    direction: 'Direction',
    status: 'Status',
  }

  // Build a map of CSV column to canonical field name and its metadata
  const buildMappingData = (): {
    mappingRow: Record<string, ReactElement | string>
    columnOrder: string[]
  } => {
    if (!mapping) return { mappingRow: {}, columnOrder: header }

    const mappingRow: Record<string, ReactElement | string> = {}
    const columnMetadata: Map<string, { field: string; isRequired: boolean }> = new Map()

    Object.entries(mapping.columns).forEach(([field, config]) => {
      if (!config || typeof config !== 'object') return

      const configObj = config as Record<string, unknown>
      const from = configObj.from

      // Handle both string and array formats
      let csvCol: string | undefined
      if (Array.isArray(from) && from.length > 0 && typeof from[0] === 'string') {
        csvCol = from[0]
      } else if (typeof from === 'string') {
        csvCol = from
      }

      if (csvCol) {
        const label = fieldLabels[field] || field
        const isRequired = requiredFields.includes(field)
        columnMetadata.set(csvCol, { field, isRequired })
        mappingRow[csvCol] = isRequired ? (
          <>
            {label} <span className="text-destructive">*</span>
          </>
        ) : (
          label
        )
      }
    })

    // Sort columns: required mapped → required unmapped → optional mapped → optional unmapped
    const sortedColumns = header.sort((a, b) => {
      const aMetadata = columnMetadata.get(a)
      const bMetadata = columnMetadata.get(b)

      const aIsMapped = !!aMetadata
      const bIsMapped = !!bMetadata
      const aIsRequired = aMetadata?.isRequired ?? false
      const bIsRequired = bMetadata?.isRequired ?? false

      // Required mapped first
      if (aIsMapped && aIsRequired && !(bIsMapped && bIsRequired)) return -1
      if (bIsMapped && bIsRequired && !(aIsMapped && aIsRequired)) return 1

      // Required unmapped second
      if (!aIsMapped && !bIsMapped) {
        const aReqInSchema = requiredFields.some(f => a.toLowerCase().includes(f.replace('_', ' ')))
        const bReqInSchema = requiredFields.some(f => b.toLowerCase().includes(f.replace('_', ' ')))
        if (aReqInSchema && !bReqInSchema) return -1
        if (bReqInSchema && !aReqInSchema) return 1
      }

      // Optional mapped third
      if (aIsMapped && !aIsRequired && !(bIsMapped && !bIsRequired)) return -1
      if (bIsMapped && !bIsRequired && !(aIsMapped && !aIsRequired)) return 1

      // Optional unmapped last (preserve original order)
      return header.indexOf(a) - header.indexOf(b)
    })

    return { mappingRow, columnOrder: sortedColumns }
  }

  const { mappingRow, columnOrder } = buildMappingData()
  const hasMappings = Object.keys(mappingRow).length > 0

  return (
    <div className="rounded-md border overflow-x-auto scrollbar-elegant">
      <table className="w-full caption-bottom text-sm text-foreground">
        <TableHeader>
          <TableRow className="bg-muted/40">
            {columnOrder.map(col => (
              <TableHead key={col} className="px-3 py-2 text-left font-medium min-w-[150px]">
                {col}
              </TableHead>
            ))}
          </TableRow>
          {mapping && hasMappings && (
            <TableRow className="bg-muted/20">
              {columnOrder.map(col => (
                <TableHead key={`mapped-${col}`} className="px-3 py-2 text-left font-normal text-xs text-muted-foreground min-w-[150px]">
                  {mappingRow[col] ? <span>{mappingRow[col]}</span> : '-'}
                </TableHead>
              ))}
            </TableRow>
          )}
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell className="px-3 py-4 text-muted-foreground" colSpan={columnOrder.length}>
                {t('preview.noRows', { defaultValue: 'No preview rows available.' })}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r, idx) => (
              <TableRow key={idx} className="odd:bg-background even:bg-accent/10">
                {columnOrder.map(col => (
                  <TableCell key={col} className="px-3 py-2 align-top text-sm min-w-[150px]">
                    {r[col] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </table>
    </div>
  )
}

export default PreviewTable
